import { useRef } from 'react';
import { useVirtualizer } from '@tanstack/react-virtual';
import type { components } from '@trackercop/contracts';
import { Table, THead, TBody, TR, TH, TD } from '@/components/ui/table';
import { Badge, type BadgeProps } from '@/components/ui/badge';
import { formatDateTime } from '@/lib/format';

type Event = components['schemas']['Event'];
type EventLevel = components['schemas']['EventLevel'];

const LEVEL_VARIANT: Record<EventLevel, NonNullable<BadgeProps['variant']>> = {
  debug: 'muted',
  info: 'info',
  warn: 'warning',
  error: 'destructive',
};

const VIRTUALISE_THRESHOLD = 500;
const ROW_HEIGHT = 36;
const VIRTUALISED_VIEWPORT = 600;

interface EventTableProps {
  events: Event[];
  onLoadMore?: () => void;
  hasMore?: boolean;
  isLoadingMore?: boolean;
}

export function EventTable({
  events,
  onLoadMore,
  hasMore,
  isLoadingMore,
}: EventTableProps) {
  if (events.length === 0) {
    return (
      <div
        className="rounded-md border bg-background p-6 text-sm text-muted-foreground"
        data-testid="event-table-empty"
      >
        No events match the current filters.
      </div>
    );
  }
  const virtualised = events.length > VIRTUALISE_THRESHOLD;
  return (
    <div className="flex flex-col gap-2" data-testid="event-table">
      {virtualised ? (
        <VirtualisedRows events={events} />
      ) : (
        <PlainRows events={events} />
      )}
      {onLoadMore && hasMore && (
        <button
          type="button"
          onClick={onLoadMore}
          disabled={isLoadingMore}
          className="self-start rounded-md border px-3 py-1.5 text-xs hover:bg-accent disabled:opacity-50"
          data-testid="event-table-load-more"
        >
          {isLoadingMore ? 'Loading…' : 'Load more'}
        </button>
      )}
    </div>
  );
}

function PlainRows({ events }: { events: Event[] }) {
  return (
    <Table>
      <THead>
        <TR>
          <TH className="w-44">When</TH>
          <TH className="w-20">Level</TH>
          <TH className="w-32">Source</TH>
          <TH>Event</TH>
          <TH className="w-40">Request</TH>
        </TR>
      </THead>
      <TBody>
        {events.map((e) => (
          <EventRow key={e.id} event={e} />
        ))}
      </TBody>
    </Table>
  );
}

function VirtualisedRows({ events }: { events: Event[] }) {
  const ref = useRef<HTMLDivElement | null>(null);
  const virtualizer = useVirtualizer({
    count: events.length,
    getScrollElement: () => ref.current,
    estimateSize: () => ROW_HEIGHT,
    overscan: 12,
  });
  return (
    <div
      ref={ref}
      style={{ height: VIRTUALISED_VIEWPORT }}
      className="overflow-auto rounded-md border"
      data-testid="event-table-virtualised"
    >
      <div
        style={{
          height: virtualizer.getTotalSize(),
          width: '100%',
          position: 'relative',
        }}
      >
        {virtualizer.getVirtualItems().map((virtualRow) => {
          const ev = events[virtualRow.index]!;
          return (
            <div
              key={ev.id}
              data-testid={`event-row-${ev.id}`}
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                transform: `translateY(${virtualRow.start}px)`,
                height: virtualRow.size,
              }}
              className="grid grid-cols-[11rem_5rem_8rem_1fr_10rem] items-center gap-2 border-b px-3 text-xs"
            >
              <span className="text-muted-foreground">
                {formatDateTime(ev.ts)}
              </span>
              <Badge variant={LEVEL_VARIANT[ev.level]}>{ev.level}</Badge>
              <span className="font-mono">{ev.source}</span>
              <span className="truncate">{ev.event}</span>
              <span className="truncate font-mono text-muted-foreground">
                {ev.requestId ?? '—'}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function EventRow({ event }: { event: Event }) {
  return (
    <TR data-testid={`event-row-${event.id}`}>
      <TD className="text-xs text-muted-foreground">{formatDateTime(event.ts)}</TD>
      <TD>
        <Badge variant={LEVEL_VARIANT[event.level]}>{event.level}</Badge>
      </TD>
      <TD className="font-mono text-xs">{event.source}</TD>
      <TD className="text-xs">{event.event}</TD>
      <TD className="font-mono text-[11px] text-muted-foreground">
        {event.requestId ?? '—'}
      </TD>
    </TR>
  );
}
